import { FC, useEffect, useState } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import { supabase } from '../../lib/supabase';
import { useRole } from '../../hooks/useRole';

interface LeaderboardEntry {
  user_id: string;
  display_name: string;
  points: number;
  rank: number;
}

interface UserProfile {
  user_id: string;
  display_name: string;
}

const Leaderboard: FC = () => {
  const [entries, setEntries] = useState<LeaderboardEntry[]>([]);
  const [employees, setEmployees] = useState<UserProfile[]>([]);
  const [loading, setLoading] = useState(true);
  const { user } = useAuth();
  const { isAdmin } = useRole();
  const [selectedUser, setSelectedUser] = useState<string>('');
  const [pointsToAdd, setPointsToAdd] = useState<number>(0);
  const [reason, setReason] = useState<string>('');
  const [message, setMessage] = useState<string>('');

  useEffect(() => {
    loadLeaderboard();
    if (isAdmin) {
      loadEmployees();
    }
  }, [isAdmin]);

  const loadEmployees = async () => {
    try {
      const { data, error } = await supabase
        .from('user_profiles')
        .select('user_id, display_name')
        .order('display_name');

      if (error) throw error;
      setEmployees(data || []);
    } catch (error) {
      console.error('Error loading employees:', error);
    }
  };

  const loadLeaderboard = async () => {
    try {
      setLoading(true);
      const { data: pointsData, error: pointsError } = await supabase
        .from('leaderboard_points')
        .select('*')
        .order('points', { ascending: false });

      if (pointsError) throw pointsError;

      const userIds = pointsData?.map(entry => entry.user_id) || [];
      const { data: profilesData, error: profilesError } = await supabase
        .from('user_profiles')
        .select('user_id, display_name')
        .in('user_id', userIds);

      if (profilesError) throw profilesError;

      // Combine and rank the data, handling ties
      let currentRank = 1;
      let currentPoints = -1;
      let offset = 0;

      const formattedData = pointsData.map((entry, index) => {
        const userProfile = profilesData?.find(profile => profile.user_id === entry.user_id);
        
        // If points are different from previous entry, update rank
        if (entry.points !== currentPoints) {
          currentRank = index + 1 - offset;
          currentPoints = entry.points;
        } else {
          // If points are the same, keep the same rank and increment offset
          offset++;
        }

        return {
          user_id: entry.user_id,
          display_name: userProfile?.display_name || 'Unknown User',
          points: entry.points,
          rank: currentRank
        };
      });

      setEntries(formattedData);
    } catch (error) {
      console.error('Error loading leaderboard:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddPoints = async () => {
    if (!selectedUser || !pointsToAdd) {
      setMessage('Please select an employee and enter points');
      return;
    }

    try {
      // First check if user already has points
      const { data: existingPoints } = await supabase
        .from('leaderboard_points')
        .select('points')
        .eq('user_id', selectedUser)
        .single();

      // Calculate new points total
      const newPoints = (existingPoints?.points || 0) + pointsToAdd;

      // Add points to history
      const { error: historyError } = await supabase
        .from('points_history')
        .insert({
          user_id: selectedUser,
          points_added: pointsToAdd,
          reason
        });

      if (historyError) throw historyError;

      // Update or insert points - removed updated_at field
      const { error: pointsError } = await supabase
        .from('leaderboard_points')
        .upsert({
          user_id: selectedUser,
          points: newPoints
        });

      if (pointsError) throw pointsError;

      setMessage('Points added successfully!');
      await loadLeaderboard();
      setSelectedUser('');
      setPointsToAdd(0);
      setReason('');
    } catch (error: any) {
      console.error('Error updating points:', error);
      setMessage(error.message || 'Error adding points');
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-64">
      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
    </div>;
  }

  return (
    <div className="max-w-4xl mx-auto p-4">
      <h1 className="text-2xl font-bold mb-6">Leaderboard Points</h1>

      {/* Admin Controls */}
      {isAdmin && (
        <div className="bg-white rounded-lg shadow p-4 mb-6">
          <h2 className="text-lg font-semibold mb-4">Add Points</h2>
          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-700">Select Employee</label>
              <select
                value={selectedUser}
                onChange={(e) => setSelectedUser(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              >
                <option value="">Select an employee</option>
                {employees.map((employee) => (
                  <option key={employee.user_id} value={employee.user_id}>
                    {employee.display_name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Points to Add</label>
              <input
                type="number"
                value={pointsToAdd}
                onChange={(e) => setPointsToAdd(parseInt(e.target.value) || 0)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
              />
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700">Reason</label>
              <input
                type="text"
                value={reason}
                onChange={(e) => setReason(e.target.value)}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-blue-500 focus:ring-blue-500"
                placeholder="Enter reason for points"
              />
            </div>

            {message && (
              <div className={`p-3 rounded-md ${
                message.includes('Error') ? 'bg-red-50 text-red-700' : 'bg-green-50 text-green-700'
              }`}>
                {message}
              </div>
            )}

            <button
              onClick={handleAddPoints}
              className="w-full bg-blue-600 text-white py-2 px-4 rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2"
            >
              Add Points
            </button>
          </div>
        </div>
      )}

      {/* Top 3 Leaderboard Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
        {[1, 2, 3].map((rank) => {
          const topRankUsers = entries.filter(entry => entry.rank && entry.rank === rank);
          const medals = ['🥇', '🥈', '🥉'];
          const colors = [
            'bg-gradient-to-br from-yellow-300 to-yellow-500',
            'bg-gradient-to-br from-gray-300 to-gray-400',
            'bg-gradient-to-br from-amber-600 to-amber-700'
          ];
          
          if (topRankUsers.length === 0) return null;

          return (
            <div 
              key={`rank-${rank}`}
              className={`${colors[rank - 1]} rounded-lg p-6 text-white shadow-lg transform hover:scale-105 transition-transform duration-200`}
            >
              <div className="flex flex-col items-center">
                <span className="text-4xl mb-2">{medals[rank - 1]}</span>
                <div className="text-center">
                  {topRankUsers.map((user, index) => (
                    <div key={user.user_id} className={index > 0 ? 'mt-2 pt-2 border-t border-white/20' : ''}>
                      <h3 className="font-bold text-xl mb-1">{user.display_name}</h3>
                      <p className="text-2xl font-bold">
                        {user.points.toLocaleString()} pts
                      </p>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* Your Current Rank Card - if user is logged in */}
      {user && (
        <div className="mb-8">
          <h2 className="text-lg font-semibold mb-4">Your Rank</h2>
          <div className="bg-gradient-to-r from-purple-500 to-purple-600 rounded-lg p-4 text-white">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-4">
                <span className="text-2xl font-bold">
                  #{entries.find(e => e.user_id === user.id)?.rank || '-'}
                </span>
                <div>
                  <p className="font-medium">{entries.find(e => e.user_id === user.id)?.display_name}</p>
                  <p className="text-sm opacity-75">Keep climbing! 🚀</p>
                </div>
              </div>
              <div className="text-2xl font-bold">
                {entries.find(e => e.user_id === user.id)?.points.toLocaleString() || 0} pts
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Rest of Leaderboard */}
      <div className="bg-white rounded-lg shadow-lg overflow-hidden mb-8">
        <div className="p-4">
          <h2 className="text-lg font-semibold mb-4">All Rankings</h2>
        </div>
        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Rank
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Employee
                </th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Points
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {entries.map((entry) => (
                <tr 
                  key={`list-${entry.user_id}`}
                  className={`hover:bg-gray-50 transition-colors duration-150 ${
                    entry.user_id === user?.id ? 'bg-blue-50' : ''
                  } ${entry.rank && entry.rank <= 3 ? 'font-semibold' : ''}`}
                >
                  <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                    <div className="flex items-center">
                      {entry.rank && entry.rank <= 3 && (
                        <span className="mr-2">
                          {['🥇', '🥈', '🥉'][entry.rank - 1]}
                        </span>
                      )}
                      {entry.rank}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <div className="text-sm font-medium text-gray-900">
                        {entry.display_name}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium text-gray-900">
                    {entry.points.toLocaleString()} pts
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      {/* Points System Rules */}
      <div className="bg-white rounded-lg shadow-lg p-6">
        <h2 className="text-2xl font-bold mb-6 flex items-center gap-2">
          🏆 How to Earn Points 🏆
        </h2>

        {/* Basic Points Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            💰 Basic Points <span className="text-gray-500">(for good sales)</span>
          </h3>
          <ul className="space-y-3">
            <li className="flex items-center gap-2">
              <span className="text-blue-600">🎯</span>
              If your sales are <span className="font-medium">higher than the average</span> for that store 
              → <span className="font-bold text-green-600">you get 5 points</span>
            </li>
            <li className="flex items-center gap-2">
              <span className="text-yellow-500">⭐</span>
              If your sales are <span className="font-medium">higher than the all-time best</span> for that store 
              → <span className="font-bold text-green-600">you get 10 points</span>
            </li>
          </ul>
        </div>

        {/* Streak Bonus Section */}
        <div className="mb-8">
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            🔥 Streak Bonus <span className="text-gray-500">(for keeping up the great work!)</span>
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg">
            <p className="mb-3">If your sales are <span className="font-medium">20% higher than the average</span>, your streak starts at 0</p>
            <p className="mb-4">If you do it again the next day, your streak goes up:</p>
            <ul className="space-y-2 ml-4">
              <li>1st day: Streak 0 <span className="text-gray-500">(no extra bonus yet)</span></li>
              <li>2nd day: Streak 1 → <span className="font-medium text-green-600">+1 extra point</span></li>
              <li>3rd day: Streak 2 → <span className="font-medium text-green-600">+2 extra points</span></li>
              <li>4th day: Streak 3 → <span className="font-medium text-green-600">+3 extra points</span></li>
              <li>5th day: Streak 4 → <span className="font-medium text-green-600">+4 extra points</span></li>
              <li>6th day and beyond: Streak 5 (maximum) → <span className="font-medium text-green-600">+5 extra points</span></li>
            </ul>
          </div>
        </div>

        {/* Warning Section */}
        <div className="bg-red-50 p-4 rounded-lg mb-8">
          <p className="text-red-600 font-medium flex items-center gap-2">
            ⚠️ BUT! If you miss the 20% goal, your streak ends!
          </p>
        </div>

        {/* Example Section */}
        <div>
          <h3 className="text-lg font-semibold mb-4 flex items-center gap-2">
            📝 Example:
          </h3>
          <div className="bg-gray-50 p-4 rounded-lg space-y-2">
            <p>👉 Day 1: You sell above 20% of the average → Streak 0, <span className="font-medium text-green-600">5 points</span> earned</p>
            <p>👉 Day 2: You do it again! → Streak 1, <span className="font-medium text-green-600">5 + 1 = 6 points</span></p>
            <p>👉 Day 3: You do it again! → Streak 2, <span className="font-medium text-green-600">5 + 2 = 7 points</span></p>
            <p>👉 Day 4: Your sales drop below 20% → <span className="font-medium text-red-600">Streak ends! 😢</span></p>
          </div>
        </div>

        {/* Summary Section */}
        <div className="mt-8 border-t pt-6">
          <h3 className="text-lg font-semibold mb-4">📋 Summary:</h3>
          <ul className="space-y-2">
            <li className="flex items-center gap-2">
              ✅ Sell more than the average = <span className="font-medium text-green-600">5 or 10 points</span>
            </li>
            <li className="flex items-center gap-2">
              ✅ Sell 20% more than the average = <span className="font-medium">start a streak</span>
            </li>
            <li className="flex items-center gap-2">
              ✅ Keep selling 20% more every day = <span className="font-medium text-green-600">extra bonus points</span> (up to +5 max)
            </li>
            <li className="flex items-center gap-2">
              ❌ Miss the goal = <span className="font-medium text-red-600">streak ends!</span>
            </li>
          </ul>
        </div>
      </div>
    </div>
  );
};

export default Leaderboard; 